import { ContentBlock, FeedItem } from '@knocklabs/client'
import { useKnockFeed } from '@knocklabs/react-notification-feed'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import xss from 'xss'
import { resizedImageUrl } from 'utils/imageUrl'
import { useTrackVisibility } from 'utils/useTrackVisibility'
import { LoadingLine } from 'lib/LoadingLine'
import { Logo as OriginalLogo } from 'lib/Logo'
import { Text, textWeight } from 'lib/Text'
import { useBrand } from 'modules/brands/queries/useBrand'
import { Clap } from './icons/Clap'
import { Generic } from './icons/Generic'
import { Learn } from './icons/Learn'
import { Love } from './icons/Love'
import { Photographer } from './icons/Photographer'
import { Reply } from './icons/Reply'
import { Review } from './icons/Review'
import { Supertester } from './icons/Supertester'

export const Row = styled.div<{ isPlaceholder?: boolean }>`
  align-items: center;
  background: white;
  cursor: pointer;
  display: flex;
  padding: 20px 8px;
  gap: 20px;
  transition: background 0.05s;
  border-bottom: 1px solid var(--black-20);
  &:hover {
    background: var(--light-yellow);
  }
  ${({ isPlaceholder }) =>
    isPlaceholder &&
    css`
      border-bottom: 0;
      cursor: auto;
      &:hover {
        background: white;
      }
    `}
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  padding-right: 32px;
  flex: 1;
  line-height: 16px;
`

const MarkAsReadButton = styled.button`
  all: unset;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 10px;
`

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: black;
`

const ImageContainer = styled.div<{ round?: boolean }>`
  align-self: flex-start;
  background: var(--black-10);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 48px;
  width: 48px;
  ${(props) =>
    props.round &&
    css`
      border-radius: 50%;
    `}
`

const Image = styled.img<{ round?: boolean }>`
  border: 1px solid var(--black-100);
  box-shadow: 0 0 0 0.5px black;
  ${(props) =>
    props.round &&
    css`
      border-radius: 50%;
    `}
`

const NoImage = styled.div`
  background: var(--light-yellow);
  border: 1px solid var(--black-100);
  box-shadow: 0 0 0 0.5px black;
  width: 100%;
  height: 100%;
`

const IconContainer = styled.div`
  border-radius: 50%;
  position: absolute;
  right: -8px;
  bottom: -8px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const NotificationText = styled(Text)`
  line-height: 16px;
  strong {
    ${textWeight('bold')}
  }
`

const Logo = styled(OriginalLogo)`
  background: black;
  height: 48px;
  width: 48px;
  padding: 4px;
  path {
    fill: white;
  }
`

type BlockByName = {
  [name: string]: ContentBlock
}

const reactionIcons = {
  Love: <Love />,
  Reply: <Reply />,
  Learn: <Learn />,
  Thank: <Clap />
}

const achievementIcons = {
  supertester: <Supertester />,
  photographer: <Photographer />
}

interface Props {
  item: FeedItem
  onClickItem?: () => void
}

export const NotificationRow = ({ item, onClickItem }: Props) => {
  const { push } = useRouter()
  const { data } = useBrand(item.data?.brandSlug)

  const { feedClient } = useKnockFeed()

  const { ref: visibilityRef } = useTrackVisibility<HTMLDivElement>({
    seenLabel: 'Notification Seen',
    seenLongLabel: 'Notification Read',
    payload: {
      id: item.id,
      type: item.source.key
    }
  })

  const blocksByName: BlockByName = useMemo(() => {
    return item.blocks.reduce((acc, block) => {
      return { ...acc, [block.name]: block }
    }, {})
  }, [item])

  const parseEventData = (item: FeedItem): { icon: React.ReactNode | null; imageUrl: string | null; isActor: boolean } => {
    switch (item.source.key) {
      case 'new-review-reply':
        return {
          icon: reactionIcons[item.data?.replyType as keyof typeof reactionIcons],
          imageUrl: resizedImageUrl(data?.brand?.primaryImageFullPath, { width: 48, height: 48 }),
          isActor: false
        }
      case 'new-review':
        return {
          icon: <Generic />,
          // If it's a batched notification, show the brand image instead of some random user's avatar
          imageUrl: item.actors.length === 1 ? item.actors[0]?.avatar : resizedImageUrl(data?.brand?.primaryImageFullPath, { width: 48, height: 48 }),
          isActor: item.actors.length === 1
        }
      case 'new-review-clap':
        return {
          icon: <Clap />,
          // If it's a batched notification, show the brand image instead of some random user's avatar
          imageUrl: item.actors.length === 1 ? item.actors[0]?.avatar : resizedImageUrl(data?.brand?.primaryImageFullPath, { width: 48, height: 48 }),
          isActor: item.actors.length === 1
        }
      case 'new-achievement':
        return {
          icon: achievementIcons[item.data?.achievementType as keyof typeof achievementIcons],
          imageUrl: item.actors[0]?.avatar,
          isActor: true
        }
      case 'product-shipped':
        return {
          icon: <Review />,
          imageUrl: resizedImageUrl(data?.brand?.primaryImageFullPath, { width: 48, height: 48 }),
          isActor: false
        }
      default:
        return {
          icon: <Generic />,
          imageUrl: null,
          isActor: false
        }
    }
  }

  const { imageUrl, icon, isActor } = parseEventData(item)

  const handleClick = async () => {
    onClickItem?.()
    await feedClient.markAsRead(item)
    window.analytics.track('Notification Clicked', {
      id: item.id,
      type: item.source.key
    })
    push(blocksByName.action_url.rendered)
  }

  const markAsRead = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    window.analytics.track('Notification Marked As Read', {
      id: item.id,
      type: item.source.key
    })
    await feedClient.markAsRead(item)
  }

  return (
    <Row onClick={handleClick} ref={visibilityRef}>
      <ImageContainer round={isActor}>
        {!imageUrl && !isActor ? <NoImage /> : <Image src={imageUrl ?? resizedImageUrl('missing.png', { width: 48, height: 48 })} round={isActor} />}
        <IconContainer>{icon}</IconContainer>
      </ImageContainer>
      <Content>
        <NotificationText color="black-100" size="pxs" dangerouslySetInnerHTML={{ __html: xss(blocksByName.body.rendered) }} />
        <NotificationText color="black-40" size="pxs">
          {item.inserted_at && dayjs(item.inserted_at).fromNow()}
        </NotificationText>

        {!item.read_at && (
          <MarkAsReadButton onClick={markAsRead} title="Mark as read">
            <Dot />
          </MarkAsReadButton>
        )}
      </Content>
    </Row>
  )
}

export const NotificationRowLoading = () => (
  <Row isPlaceholder>
    <ImageContainer />
    <Content>
      <NotificationText color="black-100" size="pxs" margin="1x">
        <LoadingLine height={4} width={200} />
      </NotificationText>
      <NotificationText color="black-40" size="pxs">
        <LoadingLine height={4} width={80} />
      </NotificationText>
    </Content>
  </Row>
)

export const NotificationRowWelcome = () => (
  <Row isPlaceholder>
    <ImageContainer>
      <Logo />
      <IconContainer>
        <Generic />
      </IconContainer>
    </ImageContainer>
    <Content>
      <NotificationText weight="bold" color="black-100" size="pxs">
        Welcome to notifications!
      </NotificationText>
      <NotificationText color="black-40" size="pxs">
        We’ll keep you up-to-date with your account and brands you’ve marked as Want To Test.
      </NotificationText>
    </Content>
  </Row>
)
